import type {ListReimbursementsStatus} from '@api/model';

/** Claim status, as the list endpoint's `status` query param accepts it. */
export type ReimbursementStatusFilter = NonNullable<ListReimbursementsStatus>;

interface StatusOption {
  value: ReimbursementStatusFilter;
  label: string;
}

/**
 * Identity function that only accepts an option list covering EVERY status.
 *
 * Two guarantees, both at compile time, both needed:
 *
 * - a status the API has and the list omits demands a `missingStatuses`
 *   property the argument cannot have, so the call fails to compile;
 * - a status the API does not have fails the constraint on `T`.
 *
 * The odd-looking intersection is there for the error message. Writing the
 * first guarantee the shorter way — collapsing the parameter type to `never` —
 * compiles and fails, but reports only "not assignable to parameter of type
 * 'never'", which tells the reader nothing about what is wrong. This form
 * reports "required in type '{ missingStatuses: \"rejected\" | \"withdrawn\" }'",
 * naming the statuses to add.
 *
 * It exists so the options can stay ONE list. Splitting labels and order into a
 * `Record` plus a tuple buys the first guarantee, loses the second, and then
 * needs a unit test to cover the seam between them.
 */
const withEveryStatus = <const T extends ReadonlyArray<StatusOption>>(
  options: T &
    (Exclude<ReimbursementStatusFilter, T[number]['value']> extends never
      ? unknown
      : {missingStatuses: Exclude<ReimbursementStatusFilter, T[number]['value']>}),
): T => options;

/**
 * The status dropdown, and the only list of statuses in the app.
 *
 * Lifecycle order, not alphabetical: the list is read as a pipeline, so
 * "Draft → Submitted → Approved → Paid" scans and "Approved, Draft, Paid,
 * Rejected" does not.
 *
 * One "Rejected" entry covers both kinds of refusal — an approver's rejection
 * and the payout desk's land on the same API status, and the difference is a
 * detail of the individual claim (its `payout_rejected_at`), not a filterable
 * category.
 *
 * The dropdown renders this, the route validates against it, and the route test
 * iterates it. Those were three hand-written copies before, which is how a
 * status the API added could start validating in the URL while staying
 * unselectable in the UI, with nothing failing.
 */
export const STATUS_OPTIONS = withEveryStatus([
  {value: 'draft', label: 'Draft'},
  {value: 'submitted', label: 'Submitted'},
  {value: 'approved', label: 'Approved'},
  {value: 'partially_approved', label: 'Partly approved'},
  {value: 'paid', label: 'Paid'},
  {value: 'rejected', label: 'Rejected'},
  {value: 'withdrawn', label: 'Withdrawn'},
]);

/** The valid `?status=` values, derived so they cannot drift from the dropdown. */
export const STATUS_VALUES: ReadonlyArray<ReimbursementStatusFilter> = STATUS_OPTIONS.map(
  (o) => o.value,
);

/**
 * `?status=` → a real status, or `undefined` for anything else.
 *
 * A search param is user-editable text in a shareable link, so junk is expected
 * traffic; forwarding it would 422 the whole list rather than one row.
 */
export function parseStatus(value: unknown): ReimbursementStatusFilter | undefined {
  return STATUS_VALUES.find((s) => s === value);
}
